import axios, { isAxiosError } from 'axios';
import {
  HEADERS,
  REVIEW_BLOCKS,
  type ApiSuccess,
  type DishLikeInput,
  type DishLikeResult,
  type MyReview,
  type MyReviews,
  type PublicReviewPage,
  type PublicReviewQuery,
  type ReviewBlock,
  type ReviewEditInput,
  type ReviewSubmitInput,
  type TrustPhotoDone,
  type TrustPhotoStartInput,
  type TrustPhotoTicket,
} from '@capsico/contracts';
import { api } from './client';

/*
 * Ratings + reviews (W17). The customer's own reviews live under /v1/customer/me/reviews (signed
 * in); a restaurant's public reviews are a guest-readable page under /v1/discover/restaurants.
 * Every POST / PATCH carries an X-Idempotency-Key (client default); submitting a review passes a
 * stable key so a retry after a dropped connection never sends a second review.
 */

const BASE = '/v1/customer/me/reviews';

export async function fetchMyReviews(): Promise<MyReviews> {
  const res = await api.get<ApiSuccess<MyReviews>>(BASE);
  return res.data.data;
}

/** Rates a delivered order; `key` is the submit's op id (reuse it when retrying the same tap). */
export async function submitReview(input: ReviewSubmitInput, key: string): Promise<MyReview> {
  const res = await api.post<ApiSuccess<MyReview>>(BASE, input, {
    headers: { [HEADERS.IDEMPOTENCY_KEY]: key },
  });
  return res.data.data;
}

export async function editReview(id: string, input: ReviewEditInput): Promise<MyReview> {
  const res = await api.patch<ApiSuccess<MyReview>>(`${BASE}/${id}`, input);
  return res.data.data;
}

/** Like / unlike one dish of a delivered order (allowed without a written review). */
export async function setDishLike(input: DishLikeInput): Promise<DishLikeResult> {
  const res = await api.post<ApiSuccess<DishLikeResult>>(`${BASE}/likes`, input);
  return res.data.data;
}

/** Review photo: ask for a short-lived PUT link, send the file straight to storage, confirm. */
export async function uploadReviewPhoto(
  file: File,
  onProgress?: (percent: number) => void,
): Promise<TrustPhotoDone> {
  const start: TrustPhotoStartInput = {
    contentType: file.type as TrustPhotoStartInput['contentType'],
    sizeBytes: file.size,
  };
  const ticket = (await api.post<ApiSuccess<TrustPhotoTicket>>(`${BASE}/photos`, start)).data.data;
  // Plain axios (not the API client): the upload goes to storage, not to the Capsico API.
  await axios.put(ticket.url, file, {
    headers: ticket.headers,
    timeout: 120_000,
    onUploadProgress: (event) => {
      if (onProgress && event.total) onProgress(Math.round((event.loaded / event.total) * 100));
    },
  });
  const done = await api.post<ApiSuccess<TrustPhotoDone>>(
    `${BASE}/photos/${ticket.photoId}/complete`,
  );
  return done.data.data;
}

/** One page of a restaurant's public reviews with the rating summary (guests may read). */
export async function fetchPublicReviews(
  outletId: string,
  query: PublicReviewQuery,
): Promise<PublicReviewPage> {
  const res = await api.get<ApiSuccess<PublicReviewPage>>(
    `/v1/discover/restaurants/${outletId}/reviews`,
    { params: query },
  );
  return res.data.data;
}

/** Why the server refused a rating (`details.block` on a rule error), or null. */
export function reviewBlock(error: unknown): ReviewBlock | null {
  if (!isAxiosError(error)) return null;
  const body = error.response?.data as { error?: { details?: { block?: unknown } } } | undefined;
  const block = body?.error?.details?.block;
  return typeof block === 'string' && (REVIEW_BLOCKS as readonly string[]).includes(block)
    ? (block as ReviewBlock)
    : null;
}
